import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Package, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState({
    adjustment: "",
    reason: "",
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Derive filtered list from state — kills the race window where a
  // background refetch briefly clobbers the user's search.
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    const searchTerms = searchQuery
      .toLowerCase()
      .split(" ")
      .filter((term) => term.length > 0);
    return products.filter((product) => {
      const searchableText = [
        product.name,
        product.plu_code || "",
        product.barcode || "",
        product.supplier_plu || "",
        product.category || "",
        product.department || "",
      ]
        .join(" ")
        .toLowerCase();
      // All search terms must be present
      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    // Navigate to Stock Master for editing
    navigate('/stock-master');
    toast.info(`Use Stock Master to edit "${product.name}"`);
  };

  const handleAddProduct = () => {
    navigate('/stock-master');
    toast.info("Use Stock Master to add new products with all fields");
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const handleStockAdjustment = async () => {
    if (!stockAdjustment.adjustment || !stockAdjustment.reason) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(
        `${API}/products/${selectedProduct.id}/adjust-stock`,
        {
          adjustment: parseFloat(stockAdjustment.adjustment),
          reason: stockAdjustment.reason,
        }
      );
      toast.success("Stock adjusted successfully");
      setStockDialogOpen(false);
      setStockAdjustment({ adjustment: "", reason: "" });
      fetchProducts();
    } catch (error) {
      console.error("Error adjusting stock:", error);
      toast.error(error.response?.data?.detail || "Failed to adjust stock");
    }
  };

  const getStockStatus = (product) => {
    if (product.current_stock <= product.minimum_level) {
      return { label: "Critical", color: "text-red-600", bg: "bg-red-50" };
    } else if (product.current_stock <= product.reorder_level) {
      return { label: "Low", color: "text-orange-600", bg: "bg-orange-50" };
    }
    return { label: "Good", color: "text-green-600", bg: "bg-green-50" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading products...</div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Products</h1>
          <p className="text-gray-600">Manage your inventory items and stock levels</p>
        </div>
        <Button
          onClick={handleAddProduct}
          data-testid="add-product-button"
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Search by name, PLU code, barcode, supplier PLU, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-2xl"
          data-testid="product-search-input"
        />
        {searchQuery && (
          <p className="text-sm text-gray-600 mt-2">
            Found {filteredProducts.length} product(s)
          </p>
        )}
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No products yet</p>
            <Button onClick={handleAddProduct} data-testid="add-first-product-button">
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Product
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-sm text-gray-400 mt-2">Opens Stock Master with all product fields</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredProducts.map((product) => {
            const status = getStockStatus(product);
            const orderQuantity = product.maximum_level - product.current_stock;
            
            return (
              <Card key={product.id} className="product-row" data-testid={`product-card-${product.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <p className="text-gray-600">{product.category}</p>
                        {product.plu_code && (
                          <span className="text-sm text-gray-500">
                            <span className="font-medium">PLU:</span> {product.plu_code}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <span className="text-sm text-gray-600">Current Stock</span>
                          <p className="text-lg font-semibold text-gray-900">
                            {product.current_stock} {product.unit}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Minimum</span>
                          <p className="text-lg font-semibold text-gray-900">
                            {product.minimum_level} {product.unit}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Reorder Level</span>
                          <p className="text-lg font-semibold text-gray-900">
                            {product.reorder_level} {product.unit}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Maximum</span>
                          <p className="text-lg font-semibold text-gray-900">
                            {product.maximum_level} {product.unit}
                          </p>
                        </div>
                      </div>
                      
                      {product.current_stock <= product.reorder_level && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="text-sm text-purple-900">
                            <span className="font-semibold">Reorder Required:</span> Order {orderQuantity} {product.unit} to reach maximum level
                          </p>
                        </div>
                      )}
                      
                      {product.supplier && (
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Supplier:</span> {product.supplier}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProduct(product);
                          setStockDialogOpen(true);
                        }}
                        data-testid={`adjust-stock-${product.id}`}
                      >
                        <Package className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent data-testid="stock-adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              Adjust stock level for {selectedProduct?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="adjustment">Adjustment Amount *</Label>
              <Input
                id="adjustment"
                type="number"
                step="0.01"
                data-testid="adjustment-input"
                value={stockAdjustment.adjustment}
                onChange={(e) =>
                  setStockAdjustment((prev) => ({ ...prev, adjustment: e.target.value }))
                }
                placeholder="Use negative for decrease"
              />
              <p className="text-sm text-gray-500 mt-1">
                Current: {selectedProduct?.current_stock} {selectedProduct?.unit}
              </p>
            </div>
            <div>
              <Label htmlFor="reason">Reason *</Label>
              <Input
                id="reason"
                data-testid="reason-input"
                value={stockAdjustment.reason}
                onChange={(e) =>
                  setStockAdjustment((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="e.g., Sales, Restocking, Damage"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStockAdjustment} data-testid="confirm-adjustment-button" className="bg-purple-600 hover:bg-purple-700">
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;